import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { X, Printer, Layers, MapPin } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Reusable fullscreen map overlay.
 * Props:
 *  - open (bool), onClose (fn)
 *  - title (string)
 *  - center [lat,lng], zoom (number)
 *  - layers: array of { id, label, color, count, render: () => ReactNode }
 *  - theme: 'light' | 'dark'
 *  - extraLegend?: array of [colorClass, label]
 */
const FullscreenMap = ({ open, onClose, title, center, zoom = 13, layers = [], theme = 'dark', extraLegend = [], children }) => {
    const [visible, setVisible] = useState(() => Object.fromEntries(layers.map(l => [l.id, true])));

    useEffect(() => {
        // Sync newly added layers to visible by default
        setVisible(prev => {
            const next = { ...prev };
            layers.forEach(l => { if (!(l.id in next)) next[l.id] = true; });
            return next;
        });
    }, [layers]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [open, onClose]);

    const toggleAll = (v) => setVisible(Object.fromEntries(layers.map(l => [l.id, v])));

    const visibleLayers = useMemo(() => layers.filter(l => visible[l.id]), [layers, visible]);

    const handlePrint = () => {
        // Hide controls before printing, then trigger print
        const printStyle = document.createElement('style');
        printStyle.id = 'fullscreen-map-print-style';
        // CSS statique sans entree utilisateur. Utilisation de textContent (vs innerHTML) pour eviter tout risque XSS.
        printStyle.textContent = `
            @media print {
                body * { visibility: hidden !important; }
                .fs-map-print, .fs-map-print * { visibility: visible !important; }
                .fs-map-print { position: fixed !important; inset: 0 !important; width: 100vw !important; height: 100vh !important; background: white !important; }
                .fs-map-no-print { display: none !important; }
                .leaflet-control-zoom, .leaflet-control-attribution { display: none !important; }
            }
        `;
        document.head.appendChild(printStyle);
        setTimeout(() => {
            window.print();
            setTimeout(() => printStyle.remove(), 500);
        }, 100);
    };

    if (!open) return null;

    const tileUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

    return (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col" data-testid="fullscreen-map-overlay">
            {/* Toolbar */}
            <div className="fs-map-no-print flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900/95 backdrop-blur">
                <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <h2 className="text-sm font-semibold text-white" data-testid="fullscreen-map-title">{title}</h2>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={handlePrint}
                        className="h-8 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                        data-testid="fullscreen-map-print-btn">
                        <Printer className="w-3.5 h-3.5 mr-1.5" /> Imprimer
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onClose}
                        className="h-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                        data-testid="fullscreen-map-close-btn">
                        <X className="w-4 h-4 mr-1" /> Fermer
                    </Button>
                </div>
            </div>

            {/* Body: sidebar + map */}
            <div className="flex-1 flex overflow-hidden">
                {/* Sidebar */}
                <aside className="fs-map-no-print w-64 border-r border-zinc-800 bg-zinc-900/95 backdrop-blur overflow-y-auto p-3 space-y-3">
                    <div className="flex items-center gap-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">
                        <Layers className="w-3.5 h-3.5" /> Couches
                    </div>
                    <div className="flex gap-2 text-[11px]">
                        <button onClick={() => toggleAll(true)} className="px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700" data-testid="fs-layers-all-on">Toutes</button>
                        <button onClick={() => toggleAll(false)} className="px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700" data-testid="fs-layers-all-off">Aucune</button>
                    </div>
                    <div className="space-y-1.5">
                        {layers.map(l => (
                            <label key={l.id}
                                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/70 cursor-pointer text-sm text-zinc-200"
                                data-testid={`fs-layer-toggle-${l.id}`}>
                                <input type="checkbox" checked={!!visible[l.id]}
                                    onChange={(e) => setVisible(v => ({ ...v, [l.id]: e.target.checked }))}
                                    className="accent-blue-500" />
                                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: l.color }}></span>
                                <span className="flex-1">{l.label}</span>
                                {typeof l.count === 'number' && (
                                    <span className="text-[11px] text-zinc-500 bg-zinc-800 px-1.5 rounded">{l.count}</span>
                                )}
                            </label>
                        ))}
                    </div>
                    {extraLegend.length > 0 && (
                        <div className="pt-3 border-t border-zinc-800 space-y-1">
                            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Legende</p>
                            {extraLegend.map(([colorClass, label]) => (
                                <div key={label} className="flex items-center gap-2 text-xs text-zinc-300">
                                    <span className={`w-2.5 h-2.5 rounded-full ${colorClass}`}></span>
                                    {label}
                                </div>
                            ))}
                        </div>
                    )}
                </aside>

                {/* Map */}
                <div className="flex-1 fs-map-print">
                    <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%', background: theme === 'light' ? '#f4f4f5' : '#09090b' }} scrollWheelZoom>
                        <TileLayer url={tileUrl} attribution='&copy; OpenStreetMap' />
                        {visibleLayers.map(l => (
                            <React.Fragment key={l.id}>{l.render()}</React.Fragment>
                        ))}
                        {children}
                    </MapContainer>
                </div>
            </div>
        </div>
    );
};

export default FullscreenMap;
